/**
 * EmailReportModal - Modal for selecting users and sending table data by email
 *
 * Features:
 * - Fetch and list all active users
 * - Search by name/email
 * - Multi-select with checkboxes
 * - Generates XLSX attachment and sends via backend
 * - Handles SMTP not configured gracefully
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import type { TableData } from '../../types/tableData'
import type { UserListItem } from '../../services/emailService'
import { getUsers, sendReportEmail } from '../../services/emailService'
import { buildEmailHtml } from '../../utils/emailTemplates'

interface EmailReportModalProps {
  data: TableData
  isOpen: boolean
  onClose: () => void
}

export const EmailReportModal: React.FC<EmailReportModalProps> = ({
  data,
  isOpen,
  onClose,
}) => {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  // Fetch users on open
  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    getUsers()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }, [isOpen])

  // Filter users by search term (local filtering)
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users
    const term = search.toLowerCase()
    return users.filter(
      u =>
        u.full_name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
    )
  }, [users, search])

  const toggleUser = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)))
    }
  }, [filteredUsers, selectedIds.size])

  const handleSend = useCallback(async () => {
    if (selectedIds.size === 0) return

    setSending(true)
    setResult(null)

    try {
      // Generate XLSX attachment
      const { generateXlsxBase64 } = await import('../../utils/exportXlsx')
      const xlsxBase64 = await generateXlsxBase64(data)

      const now = new Date()
      const dateStr = now.toLocaleDateString('pt-BR').replace(/\//g, '-')
      const timeStr = now
        .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        .replace(':', 'h')
      const slug = (data.title || 'relatorio')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
      const filename = `${slug}-${dateStr}-${timeStr}.xlsx`

      await sendReportEmail({
        subject: `${data.title || 'Relatório'} - MDi Optimus (${now.toLocaleDateString('pt-BR')})`,
        body_html: buildEmailHtml(data),
        recipient_ids: Array.from(selectedIds),
        attachment_base64: xlsxBase64,
        attachment_filename: filename,
      })

      setResult({
        type: 'success',
        message: `Email enviado para ${selectedIds.size} destinatario(s).`,
      })
      setTimeout(() => onClose(), 2000)
    } catch (err: unknown) {
      let message =
        'Erro ao enviar email. Verifique se o SMTP esta configurado.'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } }
        if (axiosErr.response?.data?.detail) {
          message = axiosErr.response.data.detail
        }
      }
      setResult({ type: 'error', message })
    } finally {
      setSending(false)
    }
  }, [data, selectedIds, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-t-2xl lg:rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="envelope" size={20} className="text-brand-500" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Enviar por Email
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <Icon name="x" size={16} className="text-gray-500" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {data.title} — selecione os destinatarios
            </p>
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          {/* User list */}
          <div className="flex-1 overflow-y-auto px-4 py-2 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" className="text-brand-500" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                Nenhum usuario encontrado
              </p>
            ) : (
              <>
                {/* Select all */}
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-brand-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors mb-1"
                >
                  {selectedIds.size === filteredUsers.length
                    ? 'Desmarcar todos'
                    : 'Selecionar todos'}
                </button>

                {filteredUsers.map(user => {
                  const isSelected = selectedIds.has(user.id)
                  return (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUser(user.id)}
                        className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.full_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </>
            )}
          </div>

          {/* Result message */}
          {result && (
            <div
              className={`mx-4 mb-2 px-3 py-2 rounded-lg text-xs font-medium ${
                result.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}
            >
              {result.message}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSend}
                disabled={selectedIds.size === 0 || sending}
                className="flex-1 px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                {sending ? (
                  <>
                    <Spinner size="xs" className="text-white" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Icon name="send" size={16} />
                    Enviar ({selectedIds.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
